// DishTech React
import {actions as reduxActions, actionTypes as reduxActionTypes} from '@dishtech-react/redux';

// Action Types
import axios from 'axios';
import {actionTypes} from '../action-types';
import {serviceConfig} from '../../../config/service-config';
import {separateUniqueRUIDsFromUserEntry, validateRuidsCollection} from '../../components/helpers/tools';

const {registerForm, setFieldValue, setFieldValues} = reduxActions;
let viewFolderName = '';

const handleAddDataPackage = (dispatch, {dataPackage}, {folder: {mode}, form}) => {
  const formKey = `${mode.toUpperCase()}_FOLDER`;
  const {
    [formKey]: {
      values: {dataPackages = []},
    },
  } = form;

  return dispatch(
    setFieldValue(formKey, 'dataPackages', [
      ...dataPackages.filter(({dataPackageId}) => dataPackageId !== dataPackage.dataPackageId),
      dataPackage,
    ])
  );
};

const handleRemoveDataPackage = (dispatch, {dataPackageId}, {folder: {mode}, form}) => {
  const formKey = `${mode.toUpperCase()}_FOLDER`;
  const {
    [formKey]: {
      values: {dataPackages = []},
    },
  } = form;

  return dispatch(
    setFieldValue(
      formKey,
      'dataPackages',
      dataPackages.filter(dataPackage => dataPackageId !== dataPackage.dataPackageId)
    )
  );
};

const resetFolder = dispatch => {
  dispatch({
    payload: {},
    type: actionTypes.FOLDER_RESET,
  });
};

const openOverlay = (dispatch, mode) => {
  if (mode !== 'delete') {
    dispatch({
      payload: {
        mode,
      },
      type: actionTypes.FOLDER_SET_MODE,
    });
  }

  dispatch({
    payload: {
      overlayId: `folder_${mode}`,
    },
    type: reduxActionTypes.OVERLAY_OPEN,
  });
};

const handleViewFolder = async (dispatch, {folderData}) => {
  let newFolderData = [];
  try {
    const {baseUrl, path, method} = serviceConfig.FOLDER_VIEW;
    const pathWithData = path.replace('{folderData.name}', folderData.name);
    const response = await axios[method.toLowerCase()](`${baseUrl}${pathWithData}`);
    viewFolderName = '';
    newFolderData = {...folderData, dataPackages: response.data.featuredFolders[0].dataPackages};
  } catch (error) {
    newFolderData = {...folderData, dataPackages: []};
  }

  Promise.resolve(dispatch(registerForm('VIEW_FOLDER'))).then(() =>
    Promise.resolve(dispatch(setFieldValues('VIEW_FOLDER', newFolderData))).then(() => {
      openOverlay(dispatch, 'view');
    })
  );
};

const handleUpdateFolder = (dispatch, {folderData}) => {
  Promise.resolve(dispatch(registerForm('UPDATE_FOLDER'))).then(() =>
    Promise.resolve(dispatch(setFieldValues('UPDATE_FOLDER', folderData))).then(() => {
      openOverlay(dispatch, 'update');
    })
  );
};

const handleCreateFolder = (dispatch, {parentFolderId}, {form}) => {
  if (parentFolderId) {
    const {
      VIEW_FOLDER: {
        values: formValues,
        values: {folderID, name, parents},
      },
    } = form;

    dispatch({
      payload: {
        folderData: formValues,
      },
      type: actionTypes.FOLDER_SET_DATA,
    });

    Promise.resolve(dispatch(registerForm('CREATE_FOLDER'))).then(() =>
      Promise.resolve(dispatch(setFieldValue('CREATE_FOLDER', 'parents', [...parents, {folderID, name}]))).then(() =>
        openOverlay(dispatch, 'create')
      )
    );
  } else {
    openOverlay(dispatch, 'create');
  }
};

const getFolder = (folders, folderIdList) => {
  if (!folderIdList.length) {
    return false;
  }

  if (folderIdList.length === 1) {
    return folders.find(folder => folder.folderID === folderIdList[0]);
  }

  return getFolder(folders.find(folder => folder.folderID === folderIdList[0]).subFolders, folderIdList.slice(1));
};

const handleServiceFulfilled = (
  dispatch,
  {serviceKey},
  {
    folder: {folderToDelete: {isSubFolder} = {}},
    form: {VIEW_FOLDER: {values: formValues} = {}},
    overlay: {folder_view: viewOverlayOpen} = {},
    service: {FOLDER: {response: {featuredFolders = []} = {}} = {}} = {},
  }
) => {
  if (serviceKey === 'DELETE_FOLDER') {
    if (!isSubFolder) {
      dispatch({
        payload: {
          overlayId: 'folder_view',
        },
        type: reduxActionTypes.OVERLAY_CLOSE,
      });
    }

    resetFolder(dispatch);
  } else if (serviceKey === 'FOLDER' && formValues && viewOverlayOpen) {
    const {folderID, parents} = formValues;
    const folderData = getFolder(featuredFolders, [...parents.map(parent => parent.folderID), folderID]);

    dispatch(
      setFieldValues('VIEW_FOLDER', {
        ...folderData,
        parents,
      })
    );
  }
};

const handleDeleteFolder = dispatch => {
  openOverlay(dispatch, 'delete');
};

const handleSetDataPackagesFromEntry = (dispatch, ruidEntryString) => {
  const separatedUniqueRUIDsFromEntry = separateUniqueRUIDsFromUserEntry(ruidEntryString);

  validateRuidsCollection(separatedUniqueRUIDsFromEntry).then(validDataPackagesCollectorMap => {
    dispatch({
      payload: {
        ruidsFromEntry: separatedUniqueRUIDsFromEntry,
        dataPackagesFromEntryMap: validDataPackagesCollectorMap,
      },
      type: actionTypes.FOLDER_SET_DATA_PACKAGES_FROM_ENTRY_DONE,
    });
  });
};

const handleAddSelectedDataPackagesFromEntryToFolder = (dispatch, selectedRuids, nextState) => {
  const {
    folder: {
      dataPackagesFromEntry: {dataPackagesFromEntryMap = new Map()},
      mode,
    },
    form,
  } = nextState;

  const formKey = `${mode.toUpperCase()}_FOLDER`;
  const {
    [formKey]: {
      values: {dataPackages = []},
    },
  } = form;

  const currentlyAddedDataPackagesMap = new Map(dataPackages.map(dp => [dp.dataPackageId, dp]));
  const currentlyAddedRuids = dataPackages
    .map(({dataPackageId}) => dataPackageId)
    .filter(dataPackageId => !dataPackagesFromEntryMap.has(dataPackageId));
  const uniqueRuids = Array.from(new Set([...currentlyAddedRuids, ...selectedRuids]));

  const dataPackageCollectionToAddToForm = uniqueRuids
    .filter(ruid => dataPackagesFromEntryMap.has(ruid) || currentlyAddedDataPackagesMap.has(ruid))
    .map(ruid => dataPackagesFromEntryMap.get(ruid) || currentlyAddedDataPackagesMap.get(ruid));

  return dispatch(setFieldValue(formKey, 'dataPackages', dataPackageCollectionToAddToForm));
};

export const folderMiddleware = store => next => action => {
  const {dispatch, getState} = store;

  next(action);

  const nextState = getState();

  const actions = {
    [actionTypes.FOLDER_ADD_DATA_PACKAGE]: () => handleAddDataPackage(dispatch, action.payload, nextState),
    [actionTypes.FOLDER_CREATE]: () => handleCreateFolder(dispatch, action.payload, nextState),
    [actionTypes.FOLDER_DELETE]: () => handleDeleteFolder(dispatch),
    [reduxActionTypes.SERVICE_FULFILLED]: () => handleServiceFulfilled(dispatch, action.payload, nextState),
    [actionTypes.FOLDER_REMOVE_DATA_PACKAGE]: () => handleRemoveDataPackage(dispatch, action.payload, nextState),
    [actionTypes.FOLDER_UPDATE]: () => handleUpdateFolder(dispatch, action.payload),
    [actionTypes.FOLDER_VIEW]: () => handleViewFolder(dispatch, action.payload),
    [actionTypes.FOLDER_SET_DATA_PACKAGES_FROM_ENTRY]: () => handleSetDataPackagesFromEntry(dispatch, action.payload),
    [actionTypes.FOLDER_ADD_DATA_PACKAGES_FROM_ENTRY_TO_FOLDER]: () =>
      handleAddSelectedDataPackagesFromEntryToFolder(dispatch, action.payload, nextState),
  };

  if (actions[action.type]) {
    if (action.type === 'FOLDER_VIEW' && viewFolderName === action.payload.folderData.name) {
      return;
    }
    viewFolderName = action.type === 'FOLDER_VIEW' ? action.payload.folderData.name : '';
    return actions[action.type]();
  }
};
